// Browser-side API client for the admin UI. Imports only types (no server modules),
// so it is safe to use from client components. The session cookie is sent automatically
// (same-origin httpOnly cookie). A 401 bounces the user to /login.
import type {
  ConversationList,
  ConversationSummary,
  Insights,
  Report,
  DudaStats,
  AdminUserInfo,
} from './types';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    // Say why. The 12-hour session JWT does not slide, so the common cause of a 401 is
    // an overnight expiry — and a silent bounce to a blank login screen reads as "the
    // tab is broken" rather than "you were signed out".
    if (typeof window !== 'undefined') window.location.href = '/login?expired=1';
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    // A CloudFront 502/504 returns an HTML error page, not JSON, so the old
    // `body.error || 'Request failed'` collapsed every infrastructure failure into
    // three meaningless words. Name the status instead.
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    if (body?.error) throw new Error(body.error);
    if (res.status === 504 || res.status === 502) {
      throw new Error(
        `The server took too long to respond (HTTP ${res.status}). This usually happens on a cold start while it scans the whole conversations table — try again in a moment.`
      );
    }
    throw new Error(`Request failed (HTTP ${res.status}).`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // Returns the raw Response so the login page can branch on status.
  login: (username: string, password: string): Promise<Response> =>
    fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }),
  logout: (): Promise<Response> => fetch('/api/logout', { method: 'POST' }),
  conversations: (): Promise<ConversationList> =>
    fetch('/api/conversations').then((r) => jsonOrThrow<ConversationList>(r)),
  // Label summary for one chat session. Not a transcript — messages aren't stored.
  conversation: (id: string): Promise<ConversationSummary> =>
    fetch(`/api/conversations/${encodeURIComponent(id)}`).then((r) =>
      jsonOrThrow<ConversationSummary>(r)
    ),
  insights: (): Promise<Insights> =>
    fetch('/api/insights', { method: 'POST' }).then((r) => jsonOrThrow<Insights>(r)),
  report: (): Promise<Report> => fetch('/api/report').then((r) => jsonOrThrow<Report>(r)),
  dudaStats: (): Promise<DudaStats> => fetch('/api/duda-stats').then((r) => jsonOrThrow<DudaStats>(r)),

  users: (): Promise<{ users: AdminUserInfo[]; me: { isAdmin: boolean; isSuperAdmin: boolean } }> =>
    fetch('/api/users').then((r) =>
      jsonOrThrow<{ users: AdminUserInfo[]; me: { isAdmin: boolean; isSuperAdmin: boolean } }>(r)
    ),
  createUser: (body: {
    username: string;
    password: string;
    isAdmin: boolean;
    isSuperAdmin?: boolean;
  }): Promise<{ ok: true }> =>
    fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<{ ok: true }>(r)),
  updateUser: (
    username: string,
    body: { password?: string; isAdmin?: boolean; isSuperAdmin?: boolean }
  ): Promise<{ ok: true }> =>
    fetch(`/api/users/${encodeURIComponent(username)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<{ ok: true }>(r)),
  deleteUser: (username: string): Promise<{ ok: true }> =>
    fetch(`/api/users/${encodeURIComponent(username)}`, { method: 'DELETE' }).then((r) =>
      jsonOrThrow<{ ok: true }>(r)
    ),
};
